import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { usePersistedQueryParams } from '@plato/app-shared/hooks/use-persisted-query-params';
import { useQueryParam } from '@plato/ui-legacy/lib/hooks/use-query-param';
import { enumParam, queryParamsConfig, stringParam } from '@plato/ui-legacy/lib/query-params';
import { eachDayOfInterval, endOfWeek, formatISO, parseISO, startOfWeek } from 'date-fns';
import { useMemo } from 'react';
import type { RouterInput } from '../../../server/types/infer-trpc-types.ts';
import type { ViewMode } from '../components/events-view-mode-selector.tsx';

const viewConfig = queryParamsConfig<{ view: ViewMode }>({
    view: enumParam({ day: 'day', week: 'week' } as const),
});

const defaultView = { view: 'day' } satisfies { view: ViewMode };

type EventsListParams = RouterInput['platoUser']['query']['events']['getList'];

type UseEventsListParamsResult = {
    params: EventsListParams;
    days: string[];
    day: string;
    setDay: (day: string) => void;
    viewMode: ViewMode;
    setViewMode: (viewMode: ViewMode) => void;
    ownerId: string;
    setOwnerId: (ownerId: string | null) => void;
};

/**
 * Hook to manage events list URL parameters and calculate query parameters.
 * Centralizes the logic for both day and week view modes, as well as owner selection.
 * The view mode is persisted, so the calendar reopens in the view the user last picked.
 *
 * @returns Object containing URL params, setters, query params, and array of days in the range
 */
export function useEventsListParams(): UseEventsListParamsResult {
    const { userId } = useAppConfig();

    const [day, setDay] = useQueryParam(
        'day',
        stringParam(),
        formatISO(new Date(), { representation: 'date' }),
    );

    const [{ view: viewMode }, setView] = usePersistedQueryParams(viewConfig, defaultView, 'events');

    const setViewMode = (view: ViewMode): void => {
        setView({ view });
    };

    const [ownerId, setOwnerIdParam] = useQueryParam('ownerId', stringParam(), userId);

    const setOwnerId = (value: string | null) => {
        setOwnerIdParam(value ?? undefined);
    };

    const { params, days } = useMemo(() => {
        if (viewMode === 'day') {
            return {
                params: { dateFrom: day, dateTo: day, ownerId },
                days: [day],
            };
        }

        // Week view: calculate the full week range
        const days = eachDayOfInterval({
            start: startOfWeek(parseISO(day), { weekStartsOn: 1 }),
            end: endOfWeek(parseISO(day), { weekStartsOn: 1 }),
        }).map((date) => formatISO(date, { representation: 'date' }));

        const dateFrom = days[0];
        const dateTo = days.at(-1);

        if (!dateFrom || !dateTo) {
            throw new Error(`expected eachDayOfInterval to return at least one element`);
        }

        return {
            params: { dateFrom, dateTo, ownerId },
            days,
        };
    }, [day, viewMode, ownerId]);

    return {
        params,
        days,
        day,
        setDay,
        viewMode,
        setViewMode,
        ownerId,
        setOwnerId,
    };
}
